import { PageShell, PageHero } from "@project/user/components/page-shell";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import { useI18n } from "@project/lib/use-i18n";
import { HERO_IMAGES } from "@project/lib/stock-images";
import {
  GALLERY_BY_CATEGORY,
  GALLERY_CATEGORY_IDS,
  type GalleryCategoryId,
} from "@project/lib/gallery-images";

const CATEGORY_LABEL_KEYS: Record<
  GalleryCategoryId,
  | "community.gallery.categories.festivalCelebrations"
  | "community.gallery.categories.gaushala"
  | "community.gallery.categories.derasar"
  | "community.gallery.categories.outdoorActivities"
> = {
  "festival-celebrations": "community.gallery.categories.festivalCelebrations",
  gaushala: "community.gallery.categories.gaushala",
  derasar: "community.gallery.categories.derasar",
  "outdoor-activities": "community.gallery.categories.outdoorActivities",
};

export default function GalleryPage() {
  const { t } = useI18n();

  return (
    <PageShell>
      <PageHero
        eyebrow={t("community.gallery.hero.eyebrow")}
        title={t("community.gallery.hero.title")}
        subtitle={t("community.gallery.hero.subtitle")}
        image={HERO_IMAGES.gallery}
      />
      <div className="mx-auto max-w-6xl px-4 py-8">
        <Tabs defaultValue={GALLERY_CATEGORY_IDS[0]} className="space-y-10">
          <div className="flex justify-center">
            <div className="flex w-full max-w-4xl justify-center overflow-x-auto pb-1">
              <TabsList className="bg-muted/40 border-border/50 inline-flex h-auto w-max min-w-0 flex-nowrap justify-center gap-1 rounded-full border p-1.5">
                {GALLERY_CATEGORY_IDS.map(categoryId => (
                  <TabsTrigger
                    key={categoryId}
                    value={categoryId}
                    className="hover:bg-background/70 rounded-full px-4 py-2 text-xs font-medium text-muted-foreground transition-all duration-200 hover:text-foreground data-[state=active]:bg-gold data-[state=active]:text-gold-foreground data-[state=active]:shadow-sm sm:px-5 sm:text-sm"
                  >
                    {t(CATEGORY_LABEL_KEYS[categoryId])}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </div>

          {GALLERY_CATEGORY_IDS.map(categoryId => {
            const images = GALLERY_BY_CATEGORY[categoryId];

            return (
              <TabsContent
                key={categoryId}
                value={categoryId}
                className="mt-0 focus-visible:outline-none focus-visible:ring-0"
              >
                {images.length ? (
                  <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {images.map(image => (
                      <figure
                        key={image.src}
                        className="group overflow-hidden rounded-2xl border border-border bg-muted shadow-sm"
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          loading="lazy"
                          className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </figure>
                    ))}
                  </div>
                ) : (
                  <p className="bg-muted/30 rounded-2xl border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
                    {t("community.gallery.empty")}
                  </p>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </PageShell>
  );
}
